import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, MapPin, Ruler, Layers, ImageOff, X, Building2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { ResponsiveImg } from "@/components/ResponsiveImg";
import {
  PROJECTS,
  sortProjects,
  PRODUCT_CATEGORIES,
  CUSTOMER_TYPES,
  type Project,
  type ProductCategory,
  type CustomerType,
} from "@/lib/projects";

const VASAKRONAN_KISTA = PROJECTS.find((p) => p.slug === "vasakronan-kista");
const VASAKRONAN_KISTA_GALLERY: string[] = VASAKRONAN_KISTA?.gallery ?? [];
const HERO_IMAGE: string =
  VASAKRONAN_KISTA_GALLERY[0] ?? PROJECTS.find((p) => p.image)?.image ?? "";



export const Route = createFileRoute("/referenser")({
  head: () => ({
    meta: [
      { title: "Referenser — utförda projekt i Sverige | BBN Nordic" },
      {
        name: "description",
        content:
          "3 500+ nöjda kunder av solfilm, säkerhetsfilm och dekorfilm. Se bilder från utförda projekt hos fastighetsägare, butiker, kontor och privatpersoner.",
      },
      { property: "og:title", content: "Referenser — utförda projekt | BBN Nordic" },
      { property: "og:description", content: "Bildgalleri från utförda installationer av sol- och dekorfilm." },
      { property: "og:url", content: "https://bbnnordic.se/referenser" },
      { property: "og:image", content: HERO_IMAGE },
    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/referenser" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Referenser — utförda projekt | BBN Nordic",
          url: "/referenser",
          description:
            "3 500+ nöjda kunder av solfilm, säkerhetsfilm och dekorfilm hos fastighetsägare, butiker, kontor och privatpersoner i hela Sverige.",
          isPartOf: { "@type": "WebSite", name: "BBN Nordic", url: "/" },
          about: {
            "@type": "Service",
            name: "Installation av professionell fönsterfilm",
            provider: { "@type": "LocalBusiness", name: "BBN Nordic AB" },
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: "https://bbnnordic.se/" },
            { "@type": "ListItem", position: 2, name: "Referenser", item: "https://bbnnordic.se/referenser" },
          ],

        }),
      },
    ],
  }),
  component: ReferenserPage,
});

function ReferenserPage() {
  return (
    <main className="bg-background">
      {/* Hero */}
      <section className="relative pt-24 pb-12 md:pt-40 md:pb-24 overflow-hidden bg-navy">
        <div className="absolute inset-0">
          <ResponsiveImg
            src={HERO_IMAGE}
            alt="Referensprojekt Vasakronan Kista — 3M Prestige 70x solfilm monterad av BBN Nordic"
            width={1024}
            height={683}
            priority="lcp"
            className="h-full w-full object-cover opacity-70"
          />

          <div className="absolute inset-0 bg-gradient-to-r from-navy/80 via-navy/55 to-navy/20" />
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-navy to-transparent" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 md:px-10" style={{ textShadow: "0 2px 16px rgba(0,0,0,0.5)" }}>
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-gold-bright font-semibold mb-4">
              Referenser
            </p>
            <h1 className="font-display text-cream text-4xl md:text-6xl lg:text-7xl tracking-tight max-w-4xl">
              3 500+ nöjda kunder — och varje projekt är vårt visitkort.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-cream/90 max-w-2xl font-light">
              Här ser du solfilm, säkerhetsfilm och dekorfilm som vi har monterat hos fastighetsägare,
              kommuner, butikskedjor, kontor och privatpersoner i hela Sverige.
              Alla bilder nedan kommer från våra utförda projekt.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center" style={{ textShadow: "none" }}>
              <Link
                to="/offert"
                className="btn-glow inline-flex items-center justify-center gap-2 bg-gold text-navy px-6 py-3 rounded-full font-semibold min-h-11"
              >
                Få gratis offert <ArrowRight size={16} />
              </Link>
              <a
                href="tel:+46771502502"
                className="inline-flex items-center justify-center gap-2 text-cream/90 px-4 py-3 font-medium hover:text-gold-bright transition-colors min-h-11"
              >
                Ring 0771 502 502
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Utvalda projekt — filtrerbart galleri */}
      <section className="py-16 md:py-24 bg-card/40 border-y border-border">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Reveal>
            <div className="mb-10 md:mb-14">
              <p className="text-xs uppercase tracking-[0.25em] text-gold font-semibold mb-3">
                Utvalda projekt
              </p>
              <h2 className="font-display text-3xl md:text-5xl text-foreground">
                Hitta projekt efter produkt och kundtyp.
              </h2>
              <p className="mt-4 max-w-2xl text-muted-foreground text-base md:text-lg">
                Vi visar bara projekt som är fotodokumenterade och som kunden har godkänt
                för publicering. Vill du se fler exempel? Be oss om
                referenser, så delar vi dem i offertunderlaget.
              </p>
            </div>
          </Reveal>
          <FilterableProjectGallery />
        </div>
      </section>




    </main>
  );
}

function Gallery({ images, altPrefix }: { images: string[]; altPrefix: string }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
      {images.map((src, i) => (
        <a
          key={src}
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          className="group relative block aspect-[4/3] overflow-hidden rounded-2xl bg-navy-light border border-border hover:border-gold/40 transition-all"
        >
          <ResponsiveImg
            src={src}
            alt={`${altPrefix} — referensprojekt ${i + 1} i Skåne/Sverige (3M-auktoriserad installation)`}
            width={1024}
            height={768}
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />

        </a>
      ))}
    </div>
  );
}

type ProductFilter = ProductCategory | "Alla";
type CustomerFilter = CustomerType | "Alla";

function FilterableProjectGallery() {
  const [productFilter, setProductFilter] = useState<ProductFilter>("Alla");
  const [customerFilter, setCustomerFilter] = useState<CustomerFilter>("Alla");
  const [activeProject, setActiveProject] = useState<Project | null>(null);

  const filtered = useMemo(() => {
    return sortProjects(
      [...PROJECTS].reverse().filter((p) => {
        if (productFilter !== "Alla" && p.productCategory !== productFilter) return false;
        if (customerFilter !== "Alla" && p.customerType !== customerFilter) return false;
        return true;
      }),
    );
  }, [productFilter, customerFilter]);

  const productOptions: ProductFilter[] = ["Alla", ...PRODUCT_CATEGORIES];
  const customerOptions: CustomerFilter[] = ["Alla", ...CUSTOMER_TYPES];

  return (
    <div>
      {/* Filter */}
      <div className="space-y-4 mb-10">
        <FilterRow
          label="Produkt"
          options={productOptions}
          value={productFilter}
          onChange={setProductFilter}
        />
        <FilterRow
          label="Kundtyp"
          options={customerOptions}
          value={customerFilter}
          onChange={setCustomerFilter}
        />
      </div>

      {/* Resultat */}
      {filtered.length === 0 ? (
        <p className="text-muted-foreground text-sm py-12 text-center border border-dashed border-border rounded-2xl">
          Det finns ännu inga publicerade projekt med den här kombinationen. Kontakta oss om referenser, så
          kan vi dela fler exempel i offertunderlaget.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {filtered.map((p) => {
            const imageCount = (p.gallery?.length ?? (p.image ? 1 : 0));
            return (
              <button
                key={p.slug}
                type="button"
                onClick={() => setActiveProject(p)}
                className="group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-navy-light hover:border-gold/60 transition-colors text-left cursor-pointer"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-card">
                  {p.image ? (
                    <ResponsiveImg
                      src={p.image}
                      alt={`${p.client} — ${p.type}, ${p.city} (${p.product})`}
                      width={1024}
                      height={768}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  ) : (
                    <div className="h-full w-full flex flex-col items-center justify-center gap-2 text-muted-foreground/70 bg-gradient-to-br from-navy-light to-card">
                      <ImageOff size={28} strokeWidth={1.3} />
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em]">
                        Foto väntar publicering
                      </span>
                    </div>
                  )}
                  {p.city && (
                    <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-navy/85 border border-gold/30 px-2.5 py-1">
                      <MapPin size={11} className="text-gold-bright" />
                      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-cream">
                        {p.city}
                      </span>
                    </div>
                  )}
                  <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 bg-gold/95 text-navy px-2.5 py-1">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] font-semibold">
                      {p.productCategory}
                    </span>
                  </div>
                  {imageCount > 1 && (
                    <div className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 bg-navy/85 border border-cream/20 px-2.5 py-1">
                      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-cream">
                        {imageCount} bilder
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex-1 flex flex-col p-5">
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold-bright mb-2 flex items-center gap-1.5">
                    <Building2 size={12} /> Kund
                  </p>
                  <h3 className="font-display text-xl md:text-2xl text-foreground tracking-tight leading-tight">
                    {p.client}
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {p.type} · {p.customerType}
                  </p>
                  <dl className="mt-4 space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-start gap-2">
                      <Layers size={14} className="text-gold mt-0.5 shrink-0" />
                      <span>{p.product}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Ruler size={14} className="text-gold mt-0.5 shrink-0" />
                      <span>{p.area}</span>
                    </div>
                  </dl>
                  {p.result && (
                    <p className="mt-4 text-sm text-muted-foreground/90 leading-relaxed border-t border-border/60 pt-4">
                      {p.result}
                    </p>
                  )}
                  <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.18em] text-gold-bright group-hover:gap-2.5 transition-all">
                    Visa alla bilder <ArrowRight size={12} />
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <p className="mt-6 text-xs text-muted-foreground">
        Visar {filtered.length} av {PROJECTS.length} publicerade projekt.
      </p>

      <ProjectLightbox project={activeProject} onClose={() => setActiveProject(null)} />
    </div>
  );
}

function ProjectLightbox({ project, onClose }: { project: Project | null; onClose: () => void }) {
  useEffect(() => {
    if (!project) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", handler);
    };
  }, [project, onClose]);

  if (!project) return null;
  const images = project.gallery && project.gallery.length > 0 ? project.gallery : [project.image];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Referensprojekt: ${project.client}`}
      className="fixed inset-0 z-[100] bg-navy/95 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
    >
      <div className="min-h-full flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 z-10 bg-navy/95 backdrop-blur border-b border-cream/10">
          <div className="mx-auto max-w-6xl px-5 md:px-10 py-4 flex items-start justify-between gap-4">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold-bright mb-1 flex items-center gap-1.5">
                <Building2 size={12} /> Kund
              </p>
              <h2 className="font-display text-2xl md:text-4xl text-cream tracking-tight leading-tight">
                {project.client}
              </h2>
              <p className="mt-1.5 text-sm text-cream/70">
                {[project.type, project.city, project.product, project.area].filter(Boolean).join(" · ")}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Stäng"
              className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full border border-cream/20 text-cream hover:bg-cream/10 transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Bildgrid */}
        <div className="flex-1 mx-auto max-w-6xl w-full px-5 md:px-10 py-6 md:py-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
            {images.map((src, i) => (
              <div key={src + i} className="overflow-hidden rounded-xl bg-navy-light border border-cream/10">
                <ResponsiveImg
                  src={src}
                  alt={`${project.client} — ${project.type} (bild ${i + 1} av ${images.length})`}
                  width={1024}
                  height={768}
                  className="h-full w-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}



function FilterRow<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: T[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mr-2 min-w-16">
        {label}
      </span>
      {options.map((opt) => {
        const active = opt === value;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            aria-pressed={active}
            className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
              active
                ? "bg-gold text-navy border-gold"
                : "bg-transparent text-muted-foreground border-border hover:border-gold/50 hover:text-foreground"
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

